<template>
  <Teleport to="body">
    <Transition name="sheet">
      <div
        v-if="show"
        class="fixed inset-0 z-50 flex flex-col justify-end"
        role="dialog"
        aria-modal="true"
        aria-label="Call Summary"
      >
        <!-- Scrim -->
        <div class="absolute inset-0 bg-black/40" @click="$emit('close')" />

        <!-- Sheet panel -->
        <div class="relative flex max-h-[90vh] w-full flex-col rounded-t-2xl bg-white dark:bg-slate-900">
          <!-- Drag handle -->
          <div class="flex shrink-0 justify-center pt-3 pb-1">
            <div class="h-1 w-10 rounded-full bg-gray-300" />
          </div>

          <!-- Header -->
          <div class="flex shrink-0 items-center justify-between px-4 py-3">
            <div>
              <h2 class="text-base font-semibold text-gray-900 dark:text-slate-100">Call Summary</h2>
              <p class="text-xs text-gray-500 dark:text-slate-400">{{ callLabel }}</p>
            </div>
            <button
              class="flex h-8 w-8 items-center justify-center rounded-full text-gray-400 transition hover:bg-gray-100 dark:hover:bg-slate-700 hover:text-gray-600 dark:hover:text-slate-300"
              aria-label="Close"
              @click="$emit('close')"
            >
              <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <!-- Action buttons row -->
          <div class="shrink-0 border-t border-gray-100 dark:border-slate-800 px-4 py-3">
            <div class="flex flex-wrap gap-2">
              <!-- Generate (shown when none/failed) -->
              <button
                v-if="canGenerate"
                class="flex items-center gap-1.5 rounded-xl bg-primary px-4 py-2 text-sm font-medium text-white transition hover:opacity-90 disabled:opacity-50"
                :disabled="isRequesting"
                @click="requestSummary"
              >
                <span v-if="isRequesting" class="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                <svg v-else class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                {{ isRequesting ? 'Requesting…' : 'Generate summary' }}
              </button>

              <!-- Generating state (shown while in-progress) -->
              <button
                v-else-if="isGenerating"
                disabled
                class="flex cursor-not-allowed items-center gap-1.5 rounded-xl bg-gray-100 dark:bg-slate-800 px-4 py-2 text-sm font-medium text-gray-500 dark:text-slate-400"
              >
                <span class="inline-block h-4 w-4 animate-spin rounded-full border-2 border-gray-400 border-t-transparent" />
                {{ generatingLabel }}
              </button>

              <!-- Refresh (shown when ready) -->
              <button
                v-else-if="canRefresh"
                class="flex items-center gap-1.5 rounded-xl border border-gray-200 dark:border-slate-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-slate-300 transition hover:bg-gray-50 dark:hover:bg-slate-800"
                :disabled="isRefreshing"
                @click="refresh"
              >
                <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Refresh
              </button>

              <!-- View transcript -->
              <button
                class="flex items-center gap-1.5 rounded-xl border border-gray-200 dark:border-slate-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-slate-300 transition hover:bg-gray-50 dark:hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-40"
                :disabled="!hasTranscript || isTranscriptLoading"
                @click="showTranscript"
              >
                <span v-if="isTranscriptLoading" class="inline-block h-4 w-4 animate-spin rounded-full border-2 border-gray-400 border-t-transparent" />
                <svg v-else class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                View transcript
              </button>
            </div>
          </div>

          <!-- Summary body (scrollable) -->
          <div class="min-h-0 flex-1 overflow-y-auto px-4 pb-6 pt-2">
            <!-- Ready: show text -->
            <template v-if="summaryStatus === 'ready' && summaryText">
              <div class="rounded-xl border border-gray-100 dark:border-slate-800 bg-gray-50 dark:bg-slate-800 p-4">
                <!-- eslint-disable-next-line vue/no-v-html -->
                <div class="prose prose-sm max-w-none text-gray-800 dark:text-slate-200" v-html="renderedSummary" />
                <p v-if="updatedAtLabel" class="mt-2 text-xs text-gray-400">{{ updatedAtLabel }}</p>
              </div>
            </template>

            <!-- No summary yet / empty -->
            <template v-else-if="summaryStatus === 'none' || summaryStatus === 'failed'">
              <p class="py-4 text-center text-sm text-gray-400">
                {{ summaryStatus === 'failed' ? 'Summary generation failed.' : 'No summary available yet.' }}
              </p>
            </template>

            <!-- In-progress: spinner -->
            <template v-else-if="summaryStatus === 'generating' || summaryStatus === 'pending'">
              <div class="flex flex-col items-center gap-3 py-8">
                <div class="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                <p class="text-sm text-gray-500 dark:text-slate-400">{{ generatingLabel }}</p>
              </div>
            </template>

            <!-- Loading initial state -->
            <template v-else>
              <div class="flex justify-center py-8">
                <div class="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              </div>
            </template>
          </div>
        </div>
      </div>
    </Transition>

    <!-- Transcript bottom sheet (nested) -->
    <Transition name="sheet">
      <div
        v-if="showTranscriptSheet"
        class="fixed inset-0 z-60 flex flex-col justify-end"
        role="dialog"
        aria-modal="true"
        aria-label="Transcript"
      >
        <div class="absolute inset-0 bg-black/40" @click="showTranscriptSheet = false" />
        <div class="relative max-h-[80vh] w-full overflow-y-auto rounded-t-2xl bg-white dark:bg-slate-900">
          <div class="flex justify-center pt-3 pb-1">
            <div class="h-1 w-10 rounded-full bg-gray-300" />
          </div>
          <div class="flex items-center justify-between px-4 py-3">
            <h2 class="text-base font-semibold text-gray-900 dark:text-slate-100">Transcript</h2>
            <button
              class="flex h-8 w-8 items-center justify-center rounded-full text-gray-400 transition hover:bg-gray-100 dark:hover:bg-slate-700"
              @click="showTranscriptSheet = false"
            >
              <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div class="px-4 pb-8">
            <p v-if="!transcriptEntries.length" class="py-4 text-center text-sm text-gray-400">
              Transcript not available.
            </p>
            <div v-else class="space-y-3">
              <div
                v-for="(entry, idx) in transcriptEntries"
                :key="idx"
                class="rounded-xl border border-gray-100 dark:border-slate-800 bg-gray-50 dark:bg-slate-800 p-3"
              >
                <div class="flex items-start gap-3">
                  <div class="w-16 shrink-0">
                    <p class="text-xs text-gray-500 dark:text-slate-400">{{ entry.timeLabel }}</p>
                    <span class="mt-1 inline-flex rounded-md bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary">
                      {{ entry.speaker }}
                    </span>
                  </div>
                  <p class="min-w-0 flex-1 text-sm text-gray-800 dark:text-slate-200">{{ entry.text }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, watch, onUnmounted } from 'vue';
import { ref as dbRef, onValue, off, type Database } from 'firebase/database';
import type { CallRecord } from '~/types/calls';
import { isCallInbound } from '~/utils/calls';
import { useContactStore } from '~/stores/contactStore';
import { fetchCallRecord } from '~/services/calls';

const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const props = defineProps<{
  show: boolean;
  call: CallRecord | null;
}>();

defineEmits<{ close: [] }>();

const contactStore = useContactStore();

const { $rtdb, $authdFetch } = useNuxtApp() as ReturnType<typeof useNuxtApp> & {
  $rtdb: unknown;
  $authdFetch: (url: string, method?: 'GET' | 'POST' | 'PUT' | 'DELETE', body?: object) => Promise<unknown>;
};

const userStore = useUserStore();

// Local copy of the call record — kept in sync with RTDB when sheet is open
const localRecord = ref<CallRecord | null>(null);
const isRequesting = ref(false);
const isRefreshing = ref(false);
const isTranscriptLoading = ref(false);
const showTranscriptSheet = ref(false);
type TranscriptEntry = {
  speaker: string;
  text: string;
  timeLabel: string;
};

const transcriptEntries = ref<TranscriptEntry[]>([]);

let unsubscribeRtdb: (() => void) | null = null;
let detailFetchPromise: Promise<void> | null = null;
let detailFetchCallId: string | null = null;

/** Normalises the summary state following the same logic as the mobile app */
type SummaryStatus = 'none' | 'pending' | 'generating' | 'ready' | 'failed';

function deriveSummaryStatus(record: CallRecord | null): SummaryStatus {
  const summary = record?.summary;
  if (!summary) return 'none';
  const rawState = (summary.state ?? summary.s)?.toLowerCase();
  if (summary.text?.trim()) return 'ready';
  if (rawState === 's' || rawState === 'success' || rawState === 'generated' || rawState === 'ready') return 'ready';
  if (rawState === 'f' || rawState === 'failed') return 'failed';
  if (rawState === 'p-r' || rawState === 'g-s' || rawState === 'pending' || rawState === 'processing' || rawState === 'generating' || rawState === 'p') return 'generating';
  if (rawState) return 'pending';
  return 'none';
}

const summaryStatus = computed<SummaryStatus>(() => deriveSummaryStatus(localRecord.value));
const summaryText = computed(() => localRecord.value?.summary?.text?.trim() || null);
const hasTranscript = computed(() => summaryStatus.value === 'ready');
const canGenerate = computed(() => summaryStatus.value === 'none' || summaryStatus.value === 'failed');
const isGenerating = computed(() => summaryStatus.value === 'generating' || summaryStatus.value === 'pending');
const canRefresh = computed(() => summaryStatus.value === 'ready');

const generatingLabel = computed(() => {
  const rawState = (localRecord.value?.summary?.state ?? localRecord.value?.summary?.s)?.toLowerCase();
  if (rawState === 'p-r') return 'Processing recording…';
  if (rawState === 'g-s') return 'Generating summary…';
  return 'Generating…';
});

const callLabel = computed(() => {
  const call = localRecord.value || props.call;
  if (!call) return '';
  const number = isCallInbound(call)
    ? (call.callerNumber || call.callerId || '')
    : (call.destinationNumber || '');
    
  if (number) {
    const contactName = contactStore.getNameForNumber(number);
    if (contactName) return `${contactName} (${number})`;
  }
  return number;
});

const updatedAtLabel = computed(() => {
  const ts = localRecord.value?.summary?.ts;
  if (!ts) return null;
  const d = new Date(ts * 1000);
  return `Updated: ${MONTH_NAMES[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
});

/** v-html-safe minimal markdown render — HTML is escaped first to prevent XSS */
const renderedSummary = computed(() => {
  const text = summaryText.value;
  if (!text) return '';

  const escaped = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

  return escaped
    .replace(/^### (.+)$/gm, '<h4 class="font-semibold mt-3 mb-1 text-sm">$1</h4>')
    .replace(/^## (.+)$/gm, '<h3 class="font-semibold mt-4 mb-1">$1</h3>')
    .replace(/^# (.+)$/gm, '<h2 class="text-lg font-bold mt-4 mb-2">$1</h2>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/^[*-] (.+)$/gm, '<li class="ml-4 list-disc mb-0.5">$1</li>')
    .replace(/\n\n/g, '</p><p class="mb-2">')
    .replace(/\n/g, '<br>');
});

function mergePreferredRecord(freshRecord: CallRecord, fallbackRecord: CallRecord | null): CallRecord {
  if (!fallbackRecord) return freshRecord;

  return {
    ...freshRecord,
    displayName: freshRecord.displayName?.trim() ? freshRecord.displayName : fallbackRecord.displayName,
    destinationNumber: freshRecord.destinationNumber || fallbackRecord.destinationNumber,
    callerNumber: freshRecord.callerNumber || fallbackRecord.callerNumber,
    callerId: freshRecord.callerId || fallbackRecord.callerId,
    did: freshRecord.did || fallbackRecord.did,
  };
}

function shouldRefreshFromCanonicalRecord(record: CallRecord | null): boolean {
  const rawState = (record?.summary?.state ?? record?.summary?.s)?.toLowerCase();
  return rawState === 's' || rawState === 'success' || rawState === 'generated' || rawState === 'ready' || rawState === 'f' || rawState === 'failed';
}

async function loadFreshRecord() {
  const callId = props.call?.id;
  if (!callId) return;
  if (detailFetchPromise && detailFetchCallId === callId) return detailFetchPromise;

  detailFetchCallId = callId;
  detailFetchPromise = (async () => {
    try {
      const freshRecord = await fetchCallRecord(callId);
      if (props.show && props.call?.id === callId) {
        localRecord.value = mergePreferredRecord(freshRecord, localRecord.value ?? props.call);
      }
    } catch (err) {
      console.error('Failed to fetch canonical call record', err);
    } finally {
      detailFetchPromise = null;
      detailFetchCallId = null;
    }
  })();

  return detailFetchPromise;
}

// Subscribe to RTDB record updates whenever the sheet is open
function subscribeToRecord() {
  unsubscribeRtdb?.();
  unsubscribeRtdb = null;

  const userId = userStore.userDetails?.userId;
  const callId = props.call?.id;
  if (!userId || !callId) return;

  const db = $rtdb as unknown as Database;
  const recordRef = dbRef(db, `callRecords/${userId}/records/${callId}`);

  const listener = onValue(recordRef, (snapshot) => {
    const data = snapshot.val();
    if (data) {
      localRecord.value = mergePreferredRecord({ id: callId, ...data } as CallRecord, localRecord.value ?? props.call);
      if (shouldRefreshFromCanonicalRecord(localRecord.value)) {
        void loadFreshRecord();
      }
    }
  });

  unsubscribeRtdb = () => off(recordRef, 'value', listener);
}

function unsubscribe() {
  unsubscribeRtdb?.();
  unsubscribeRtdb = null;
}

watch(() => props.show, (visible) => {
  if (visible) {
    // Seed with the prop data immediately, then subscribe for live updates
    localRecord.value = props.call;
    showTranscriptSheet.value = false;
    transcriptEntries.value = [];
    subscribeToRecord();
    void loadFreshRecord();
  } else {
    unsubscribe();
    localRecord.value = null;
    detailFetchPromise = null;
    detailFetchCallId = null;
  }
});

onUnmounted(unsubscribe);

async function requestSummary() {
  const callId = props.call?.id;
  if (!callId || isRequesting.value) return;
  isRequesting.value = true;
  try {
    await $authdFetch('/api/secure/call-summary', 'POST', { callId });
    void loadFreshRecord();
  } catch (err) {
    console.error('Failed to request summary', err);
  } finally {
    isRequesting.value = false;
  }
}

async function refresh() {
  try {
    isRefreshing.value = true;
    await loadFreshRecord();
  } finally {
    isRefreshing.value = false;
  }
}

async function showTranscript() {
  const callId = props.call?.id;
  if (!callId) return;

  if (transcriptEntries.value.length) {
    showTranscriptSheet.value = true;
    return;
  }

  isTranscriptLoading.value = true;
  try {
    const data = await $authdFetch(`/api/secure/call-transcript?callId=${encodeURIComponent(callId)}`) as unknown;
    if (Array.isArray(data)) {
      transcriptEntries.value = (data as Array<{
        speaker?: string;
        speakerLabel?: string;
        channel?: string;
        text?: string;
        content?: string;
        start_time?: number;
        startTime?: number;
      }>).map((item) => ({
        speaker: formatTranscriptSpeaker(item),
        text: item.text || item.content || '',
        timeLabel: formatTranscriptOffset(item.start_time ?? item.startTime ?? 0),
      }));
    }
  } catch (err) {
    console.error('Failed to load transcript', err);
    transcriptEntries.value = [];
  } finally {
    isTranscriptLoading.value = false;
    if (transcriptEntries.value.length) showTranscriptSheet.value = true;
  }
}

function formatTranscriptSpeaker(entry: {
  speaker?: string;
  speakerLabel?: string;
  channel?: string;
}): string {
  const explicitSpeaker = entry.speaker || entry.speakerLabel;
  if (explicitSpeaker?.trim()) return explicitSpeaker.trim();

  const channel = entry.channel?.trim().toLowerCase();
  if (!channel) return 'Speaker';

  const match = channel.match(/channel[_-]?(\d+)/i);
  if (match) {
    return `Speaker ${match[1]}`;
  }

  return channel.replace(/[_-]+/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatTranscriptOffset(seconds: number): string {
  const safeSeconds = Number.isFinite(seconds) ? Math.max(0, seconds) : 0;
  const roundedSeconds = Math.floor(safeSeconds);
  const hours = Math.floor(roundedSeconds / 3600);
  const minutes = Math.floor((roundedSeconds % 3600) / 60);
  const secs = roundedSeconds % 60;

  if (hours > 0) {
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  }

  return `${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}
</script>

<style scoped>
.sheet-enter-active,
.sheet-leave-active {
  transition: opacity 0.25s ease;
}
.sheet-enter-active .relative,
.sheet-leave-active .relative {
  transition: transform 0.3s ease;
}
.sheet-enter-from,
.sheet-leave-to {
  opacity: 0;
}
.sheet-enter-from .relative {
  transform: translateY(100%);
}
.sheet-leave-to .relative {
  transform: translateY(100%);
}
</style>
